import { useMemo } from "react";
import { useAnalytics } from "context/AnalyticsContext";
import { usePress } from "context/PressContext";

/**
 * A custom hook to get the display name of an entity (competitor or press source) from its ID.
 * It centralizes the logic for looking up names from context, providing a single source of truth.
 *
 * @param {('competitor'|'news')} source - The type of entity.
 * @param {string|null} entityId - The ID of the entity to look up.
 * @returns {string} The display name of the entity or a sensible fallback.
 */
const useEntityName = (source, entityId) => {
  const { competitors } = useAnalytics();
  const { pressOptions } = usePress();

  return useMemo(() => {
    if (!entityId) {
      if (source === "news") return "All Press Sources";
      return "the selected entity";
    }

    if (source === "competitor") {
      const competitor = competitors?.find((c) => c.id === entityId);
      return competitor?.name || "the selected competitor";
    }

    if (source === "news") {
      const press = pressOptions?.find((p) => p.id === entityId);
      return press?.name || "the selected press source";
    }

    return "the selected entity";
  }, [source, entityId, competitors, pressOptions]);
};

export default useEntityName; 